import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, X, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FilterBar } from '@/components/shared/FilterBar';
import { useFilterBarSelectAll } from '@/components/shared/filterBarSelectAll';
import { DataTable } from '@/components/shared/DataTable';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/shared/tablePaginationConstants';
import { useWarehouseId } from '@/contexts/WarehouseContext';
import { useCurrency } from '@/hooks/useCurrency';
import { ordersService } from '@/modules/orders/services/orders.service';
import type { SentOrderDetail, SentOrderRow } from '@/modules/orders/services/orders.types';

const PAGE_SIZE = DEFAULT_TABLE_PAGE_SIZE;

function formatDate(dateValue: string | null): string {
  if (!dateValue) return '-';
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return dateValue;
  return parsed.toLocaleDateString('es-ES');
}

export function OrdersPage() {
  const { t } = useTranslation();
  const { format: formatMoney } = useCurrency();
  const warehouseId = useWarehouseId();
  const [rows, setRows] = useState<SentOrderRow[]>([]);
  const [count, setCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);

  const [supplierOptions, setSupplierOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [userOptions, setUserOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [minDate, setMinDate] = useState<string | null>(null);
  const [maxDate, setMaxDate] = useState<string | null>(null);

  const [supplierIds, setSupplierIds] = useState<number[]>([]);
  const [userIds, setUserIds] = useState<number[]>([]);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const {
    selectAllByFilter,
    updateSelectAllFromChange,
    clearSelectAllFor,
    clearAllSelectAll,
  } = useFilterBarSelectAll();

  const [selectedOrder, setSelectedOrder] = useState<SentOrderDetail | null>(null);
  const [selectedOrderRow, setSelectedOrderRow] = useState<SentOrderRow | null>(null);
  const [isDetailSidebarOpen, setIsDetailSidebarOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailCurrentPage, setDetailCurrentPage] = useState(1);
  const [detailPageSize, setDetailPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);

  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  const detailTotalCount = selectedOrder?.items.length ?? 0;
  const detailTotalPages = Math.max(1, Math.ceil(detailTotalCount / detailPageSize));
  const detailPageStart = (detailCurrentPage - 1) * detailPageSize;
  const detailPageItems = useMemo(
    () => (selectedOrder?.items ?? []).slice(detailPageStart, detailPageStart + detailPageSize),
    [selectedOrder?.items, detailPageStart, detailPageSize]
  );

  useEffect(() => {
    let cancelled = false;
    const loadFilters = async () => {
      setLoadingFilters(true);
      try {
        const response = await ordersService.getFilters(warehouseId);
        if (cancelled) return;
        setSupplierOptions(response.suppliers);
        setUserOptions(response.users);
        setMinDate(response.date_range.min_date);
        setMaxDate(response.date_range.max_date);
      } catch (error) {
        console.error('[OrdersPage] error loading filters', error);
      } finally {
        if (!cancelled) setLoadingFilters(false);
      }
    };
    void loadFilters();
    return () => {
      cancelled = true;
    };
  }, [warehouseId]);

  useEffect(() => {
    let cancelled = false;
    const loadOrders = async () => {
      setLoading(true);
      try {
        const response = await ordersService.getOrders({
          page: currentPage,
          page_size: pageSize,
          warehouseId,
          supplierIds,
          userIds,
          dateFrom: dateFrom || null,
          dateTo: dateTo || null,
        });
        if (cancelled) return;
        setRows(response.results);
        setCount(response.count);
      } catch (error) {
        console.error('[OrdersPage] error loading orders', error);
        if (!cancelled) {
          setRows([]);
          setCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void loadOrders();
    return () => {
      cancelled = true;
    };
  }, [currentPage, pageSize, warehouseId, supplierIds, userIds, dateFrom, dateTo]);

  const activeFilters = useMemo(() => {
    const suppliers = supplierIds
      .map((id) => supplierOptions.find((opt) => opt.value === id))
      .filter((opt): opt is { value: number; label: string } => Boolean(opt))
      .map((opt) => ({ id: 'supplier_id', label: opt.label, value: String(opt.value) }));

    const users = userIds
      .map((id) => userOptions.find((opt) => opt.value === id))
      .filter((opt): opt is { value: number; label: string } => Boolean(opt))
      .map((opt) => ({ id: 'user_id', label: opt.label, value: String(opt.value) }));

    const dates = [];
    if (dateFrom) dates.push({ id: 'date_range', label: `${t('orders.filters.fromDate')} ${dateFrom}`, value: 'from' });
    if (dateTo) dates.push({ id: 'date_range', label: `${t('orders.filters.toDate')} ${dateTo}`, value: 'to' });

    return [...suppliers, ...users, ...dates];
  }, [supplierIds, userIds, supplierOptions, userOptions, dateFrom, dateTo, t]);

  const orderColumns = useMemo(
    () => [
      {
        header: t('orders.table.orderNumber'),
        accessor: 'numero_orden',
        width: '110px',
        cell: (value: string) => <span className="font-mono text-gray-900">{value}</span>,
      },
      {
        header: t('orders.table.supplier'),
        accessor: 'proveedor_name',
        width: '190px',
        cell: (value: string) => <span className="text-gray-900 truncate block">{value}</span>,
      },
      {
        header: t('orders.table.orderDate'),
        accessor: 'fecha',
        width: '110px',
        cell: (value: string) => <span className="text-gray-700">{formatDate(value)}</span>,
      },
      {
        header: t('orders.table.itemsCount'),
        accessor: 'items_count',
        width: '90px',
        cell: (value: number) => <span className="text-gray-900">{value}</span>,
      },
      {
        header: t('orders.table.totalAmount'),
        accessor: 'total_amount',
        width: '110px',
        cell: (value: number) => <span className="text-gray-900">{formatMoney(Number(value || 0))}</span>,
      },
      {
        header: t('orders.table.user'),
        accessor: 'usuario_creador_name',
        width: '135px',
        cell: (value: string) => <span className="truncate block text-gray-700">{value}</span>,
      },
      {
        header: t('orders.table.erp'),
        accessor: 'erp_status_state',
        width: '72px',
        cell: (value: 'success' | 'warning' | 'error') =>
          value === 'success' ? (
            <div className="flex justify-center">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            </div>
          ) : value === 'warning' ? (
            <div className="flex justify-center">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
            </div>
          ) : (
            <div className="flex justify-center">
              <XCircle className="h-4 w-4 text-red-600" />
            </div>
          ),
      },
    ],
    [t]
  );

  const itemColumns = useMemo(
    () => [
      { header: t('orders.table.code'), accessor: 'producto_code', width: '90px' },
      { header: t('orders.table.product'), accessor: 'producto_name', width: '220px' },
      { header: t('orders.table.suggestedQty'), accessor: 'suggested_quantity', width: '100px' },
      { header: t('orders.table.finalQty'), accessor: 'cantidad', width: '90px' },
      { header: t('orders.table.unit'), accessor: 'unit_name', width: '80px' },
      {
        header: t('orders.table.unitPrice'),
        accessor: 'precio_estimado_unitario',
        width: '110px',
        cell: (value: number) => <span>{formatMoney(Number(value || 0))}</span>,
      },
      {
        header: t('orders.table.lineTotal'),
        accessor: 'total_value',
        width: '110px',
        cell: (value: number) => <span>{formatMoney(Number(value || 0))}</span>,
      },
    ],
    [t]
  );

  const handleOpenOrderDetail = async (order: SentOrderRow) => {
    setIsDetailSidebarOpen(true);
    setSelectedOrderRow(order);
    setSelectedOrder(null);
    setDetailError(null);
    setDetailCurrentPage(1);
    setLoadingDetail(true);
    try {
      const detail = await ordersService.getOrderDetail(order.id);
      setSelectedOrder(detail);
    } catch (error) {
      console.error('[OrdersPage] error loading order detail', error);
      setDetailError(t('orders.detail.loadError'));
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeDetailSidebar = () => {
    setIsDetailSidebarOpen(false);
    setSelectedOrder(null);
    setSelectedOrderRow(null);
    setDetailError(null);
    setLoadingDetail(false);
    setDetailCurrentPage(1);
  };

  useEffect(() => {
    if (detailCurrentPage > detailTotalPages) {
      setDetailCurrentPage(detailTotalPages);
    }
  }, [detailCurrentPage, detailTotalPages]);

  useEffect(() => {
    setCurrentPage(1);
    setIsDetailSidebarOpen(false);
    setSelectedOrder(null);
    setSelectedOrderRow(null);
    setDetailError(null);
    setDetailCurrentPage(1);
  }, [warehouseId]);

  return (
    <>
      <div className="p-4 md:p-6 space-y-4">
        <FilterBar
          filters={[
            {
              id: 'supplier_id',
              name: t('orders.filters.supplier'),
              options: supplierOptions.map((option) => ({ value: option.value, label: option.label })),
              multiple: true,
            },
            {
              id: 'user_id',
              name: t('orders.filters.user'),
              options: userOptions.map((option) => ({ value: option.value, label: option.label })),
              multiple: true,
            },
          ]}
          activeFilters={activeFilters}
          selectAllByFilter={selectAllByFilter}
          onFilterChange={(next, meta) => {
            updateSelectAllFromChange(next, meta);
            if (Object.prototype.hasOwnProperty.call(next, 'supplier_id')) {
              setSupplierIds((next.supplier_id || []).map((value) => Number(value)).filter((value) => !Number.isNaN(value)));
              setCurrentPage(1);
            }
            if (Object.prototype.hasOwnProperty.call(next, 'user_id')) {
              setUserIds((next.user_id || []).map((value) => Number(value)).filter((value) => !Number.isNaN(value)));
              setCurrentPage(1);
            }
          }}
          onClearFilter={(id) => {
            if (id === 'supplier_id') {
              setSupplierIds([]);
              clearSelectAllFor('supplier_id');
            }
            if (id === 'user_id') {
              setUserIds([]);
              clearSelectAllFor('user_id');
            }
            if (id === 'date_range') {
              setDateFrom('');
              setDateTo('');
            }
            setCurrentPage(1);
          }}
          onClearAllFilters={() => {
            setSupplierIds([]);
            setUserIds([]);
            setDateFrom('');
            setDateTo('');
            clearAllSelectAll();
            setCurrentPage(1);
          }}
        />

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label htmlFor="orders-date-from" className="text-xs font-medium text-gray-600">
                {t('orders.filters.fromOrderDate')}
              </label>
              <input
                id="orders-date-from"
                type="date"
                value={dateFrom}
                min={minDate ?? undefined}
                max={maxDate ?? undefined}
                onChange={(event) => {
                  setDateFrom(event.target.value);
                  setCurrentPage(1);
                }}
                className="mt-0.5 block w-full pl-2.5 pr-2 py-1.5 text-sm border border-gray-300 focus:outline-none focus:ring-[#1681ba] focus:border-[#1681ba] rounded-md bg-white"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="orders-date-to" className="text-xs font-medium text-gray-600">
                {t('orders.filters.toOrderDate')}
              </label>
              <input
                id="orders-date-to"
                type="date"
                value={dateTo}
                min={minDate ?? undefined}
                max={maxDate ?? undefined}
                onChange={(event) => {
                  setDateTo(event.target.value);
                  setCurrentPage(1);
                }}
                className="mt-0.5 block w-full pl-2.5 pr-2 py-1.5 text-sm border border-gray-300 focus:outline-none focus:ring-[#1681ba] focus:border-[#1681ba] rounded-md bg-white"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">{t('orders.table.title')}</h3>
            <span className="text-sm text-gray-500">{count} {t('orders.table.records')}</span>
          </div>

          {loading || loadingFilters ? (
            <div className="py-10 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1681ba]" />
            </div>
          ) : (
            <DataTable
              columns={orderColumns}
              data={rows}
              compact
              pagination
              externalPagination={{
                currentPage,
                totalPages,
                totalCount: count,
                pageSize,
                onPageChange: setCurrentPage,
                onPageSizeChange: (size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                },
              }}
              onRowClick={handleOpenOrderDetail}
            />
          )}
        </div>
      </div>

      {isDetailSidebarOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/30" onClick={closeDetailSidebar} aria-hidden />
          <aside className="fixed inset-y-0 right-0 z-50 w-full max-w-3xl bg-white shadow-2xl border-l border-gray-200 flex flex-col">
            <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-gray-900">{t('orders.detail.title')}</h3>
                {(selectedOrder || selectedOrderRow) && (
                  <p className="text-sm text-gray-500 mt-0.5">
                    {(selectedOrder?.numero_orden || selectedOrderRow?.numero_orden) ?? '-'} - {(selectedOrder?.proveedor_name || selectedOrderRow?.proveedor_name) ?? '-'}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={closeDetailSidebar}
                className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Cerrar panel"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {loadingDetail ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1681ba]" />
              </div>
            ) : detailError ? (
              <div className="flex-1 flex items-center justify-center px-6">
                <p className="text-sm text-red-600">{detailError}</p>
              </div>
            ) : !selectedOrder ? (
              <div className="flex-1 flex items-center justify-center px-6">
                <p className="text-sm text-gray-500">{t('orders.detail.empty')}</p>
              </div>
            ) : (
              <div className="p-5 overflow-y-auto space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                    <p className="text-xs text-gray-500">{t('orders.detail.orderDate')}</p>
                    <p className="text-lg font-semibold text-gray-900">{formatDate(selectedOrder.fecha)}</p>
                  </div>
                  <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                    <p className="text-xs text-gray-500">{t('orders.detail.user')}</p>
                    <p className="text-lg font-semibold text-gray-900">{selectedOrder.usuario_creador_name}</p>
                  </div>
                  <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                    <p className="text-xs text-gray-500">{t('orders.detail.erpStatus')}</p>
                    <p
                      className={`text-lg font-semibold ${
                        selectedOrder.erp_status_state === 'success'
                          ? 'text-green-700'
                          : selectedOrder.erp_status_state === 'warning'
                            ? 'text-amber-600'
                            : 'text-red-700'
                      }`}
                    >
                      {selectedOrder.erp_status_state === 'success'
                        ? t('orders.detail.erpOk')
                        : selectedOrder.erp_status_state === 'warning'
                          ? t('orders.detail.erpWarning')
                          : t('orders.detail.erpError')}
                    </p>
                  </div>
                  <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                    <p className="text-xs text-gray-500">{t('orders.detail.totalAmount')}</p>
                    <p className="text-lg font-semibold text-gray-900">{formatMoney(selectedOrder.total_amount)}</p>
                  </div>
                </div>

                <DataTable
                  columns={itemColumns}
                  data={detailPageItems}
                  compact
                  pagination
                  externalPagination={{
                    currentPage: detailCurrentPage,
                    totalPages: detailTotalPages,
                    totalCount: detailTotalCount,
                    pageSize: detailPageSize,
                    onPageChange: setDetailCurrentPage,
                    onPageSizeChange: (size) => {
                      setDetailPageSize(size);
                      setDetailCurrentPage(1);
                    },
                  }}
                />
              </div>
            )}
          </aside>
        </>
      )}
    </>
  );
}
